import { scoreToColor } from './areaData.js';
import { getListingImage } from './listingPlaceholder.js';
import { getAreaLabel } from './listingStory.js';
import { COMMUTE_MODE_ICON_PATHS, commuteModeLabel, fetchCommuteMinutes } from './commute.js';
import './commute.css';

// Kartvy's hover card for one listing, shown in the same box as the area card (#popup-hover-container)
// while a single listing on the map is hovered. Returns HTML: the box is filled with innerHTML.

export const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

// The card is small: the 640px photo (sharp at 2x) loads much faster than the 1440px one.
const smallImage = (url) => String(url || '').replace(/_\d+x0\.(jpe?g|png|webp)$/i, '_640x0.$1');

// The card's photo for a listing (the house placeholder when it has none).
export const listingHoverImageUrl = (listing) => smallImage(getListingImage(listing));

// Photos already fetched ahead (the browser keeps them: bcdn.se sends a one-year max-age).
const prefetchedImages = new Set();
const MAX_PREFETCHED = 3000;

// Fetches the card photos of these listings in the background, so hovering one shows its photo at once
// (the image host takes 0.3–0.9 s for a photo nobody has asked for yet).
export const prefetchListingHoverImages = (listings) => {
    if (typeof Image === 'undefined') return;
    (listings || []).forEach((listing) => {
        const url = listingHoverImageUrl(listing);
        if (!url || url.startsWith('data:') || prefetchedImages.has(url)) return;
        if (prefetchedImages.size >= MAX_PREFETCHED) prefetchedImages.clear();
        prefetchedImages.add(url);
        const image = new Image();
        image.decoding = 'async';
        image.fetchPriority = 'low';
        image.src = url;
    });
};

const formatNumber = (value) => Number(value).toLocaleString('sv-SE', { maximumFractionDigits: 1 }).replace(/\u00a0/g, ' ');

const TYPE_LABELS = {
    apartment: ['Lägenhet', 'Apartment'],
    rowhouse: ['Par/radhus', 'Row house'],
    house: ['Villa', 'House'],
};

// Score in half steps, 1–6, or null.
const normalizeScore = (value) => {
    const number = Number(String(value ?? '').replace(',', '.'));
    if (value == null || value === '' || !Number.isFinite(number)) return null;
    return Math.round(Math.min(Math.max(number, 1), 6) * 2) / 2;
};

// Dark text on the light score colours, white on the dark ones.
const readableTextOn = (hex) => {
    const value = String(hex || '').replace('#', '');
    if (value.length !== 6) return '#ffffff';
    const [r, g, b] = [0, 2, 4].map((i) => parseInt(value.slice(i, i + 2), 16) / 255);
    return (0.299 * r + 0.587 * g + 0.114 * b) > 0.6 ? '#13241f' : '#ffffff';
};

export const buildListingHoverCard = (listing, { score, swedish }) => {
    const title = listing.address || listing.title || (swedish ? 'Okänd adress' : 'Unknown address');
    const place = [listing.ort, listing.kommun].filter(Boolean).join(', ') || listing.subtitle || '';
    const price = Number(listing.priceValue);
    const priceText = Number.isFinite(price) && price > 0
        ? `${formatNumber(price)} kr`
        : (swedish ? 'Pris saknas' : 'No price');
    const [typeSv, typeEn] = TYPE_LABELS[listing.type] || TYPE_LABELS.house;
    const rooms = Number(listing.roomsValue ?? listing.rooms);
    const area = Number(listing.areaValue ?? listing.area);
    const fee = Number(listing.feeValue);
    const facts = [
        Number.isFinite(rooms) && rooms > 0 ? `${formatNumber(rooms)} ${swedish ? 'rum' : 'rooms'}` : (swedish ? typeSv : typeEn),
        Number.isFinite(area) && area > 0 ? `${formatNumber(area)} m²` : '',
        Number.isFinite(fee) && fee > 0 ? `${formatNumber(fee)} kr/${swedish ? 'mån' : 'mo'}` : '',
    ].filter(Boolean).join(' · ');
    const normalizedScore = normalizeScore(score);
    const scoreColor = normalizedScore != null ? (scoreToColor[normalizedScore] || '#8f9aab') : null;
    const areaLabel = getAreaLabel(normalizedScore, swedish);
    const scoreHtml = normalizedScore != null ? `
        <span class="listing-hover-score">
            <b style="background:${scoreColor};color:${readableTextOn(scoreColor)}">${formatNumber(normalizedScore)}</b>
            ${escapeHtml(areaLabel ? `${areaLabel} ${swedish ? 'område' : 'area'}` : (swedish ? 'Områdesbetyg' : 'Area score'))}
        </span>` : '';
    return `
        <div class="listing-hover-card">
            <img class="listing-hover-image" src="${escapeHtml(listingHoverImageUrl(listing))}" alt="" />
            <div class="listing-hover-main">
                <div class="listing-hover-title">${escapeHtml(title)}</div>
                <div class="listing-hover-price">${escapeHtml(priceText)}</div>
                <div class="listing-hover-place">${escapeHtml(place)}</div>
                <div class="listing-hover-facts">${escapeHtml(facts)}</div>
                ${scoreHtml}
                <span class="commute-chip listing-hover-commute" data-listing-id="${escapeHtml(listing.id)}" hidden></span>
            </div>
        </div>`;
};

// Fills the card's travel-time chip (beside the area score, like listvy's cards) once the time is known; nothing without a workplace.
// `root` holds the card's HTML; a card swapped for another listing in the meantime is left alone.
export const fillListingHoverCommute = (root, listing, swedish) => {
    const slot = root?.querySelector('.listing-hover-commute');
    if (!slot || !listing) return;
    const id = String(listing.id);
    fetchCommuteMinutes(listing)
        .then((result) => {
            if (!result || result.minutes == null || !slot.isConnected || slot.dataset.listingId !== id) return;
            const label = commuteModeLabel(result.mode, swedish, true);
            slot.innerHTML = '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" '
                + `stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${COMMUTE_MODE_ICON_PATHS[result.mode]}"/></svg>`
                + `${escapeHtml(result.minutes)} min ${escapeHtml(label)}`;
            slot.title = swedish ? `${result.minutes} min till jobbet, ${label}` : `${result.minutes} min to work, ${label}`;
            slot.hidden = false;
        })
        .catch(() => {});
};
